/**
 * DecalLayer — the passive-aftermath ground-mark manager (spec:
 * `docs/plans/fx-grammar-specs/ground-marks-and-decals.md` §2.2 / §5).
 *
 * Leak-proof BY CONSTRUCTION (the anti-ice_wall property, spec §0): a
 * fixed-size ring buffer of `DECAL_CAP` marks — at capacity the oldest is
 * evicted with a fast fade. A decal's lifetime is nobody's gameplay concern,
 * so this manager owns it outright; there is no persistent-play protocol, no
 * external stop path, and nothing to forget.
 *
 * Rendering model (spec §5.1 — bake once, sprite forever): each mark's
 * family draw program runs ONCE into a Graphics, bakes to a cached texture
 * (`bakeGraphicsToSprite` → `renderer.generateTexture`), and lives as a
 * static sprite. Cache keyed (family, school, core color, size bucket) —
 * repeated fireballs on one map share one GPU texture; per-mark seed jitter
 * applies as sprite rotation/flip, never per-mark textures. Zero per-frame
 * draw cost, no ticking system.
 *
 * Layering: one `createWorldLayerContainer(Z_LAYERS.GROUND_DECALS)` — marks
 * read as part of the ground (above platform surfaces, below vegetation,
 * shadows, zones, and every sprite). One `setLayerAlpha` dims the whole
 * aftermath layer. NEVER `style.glow` (the glow trap would reroute above the
 * player); additive families set sprite `blendMode: 'add'` instead.
 *
 * Map-switch hygiene (spec §5.3): the in-place server map switch
 * (`EVENTS.MAP_SERVER_SWITCH_START`) runs no VFX scene teardown, so the
 * layer subscribes it directly and self-clears.
 */

import { EventBus, EVENTS } from '../../../shared/EventBus';
import { gameLogger } from '../../../utils/GameLogger';
import { VisualLifecycleStore } from '../../diagnostics/VisualLifecycleStore';
import { Z_LAYERS } from '../../rendering/Z_LAYERS';
import { PixiOverlayHost } from '../../rendering/PixiOverlayHost';
import { createSprite } from '../../rendering/utils/Sprite';
import { TweenHost, type TweenHostHandle } from '../../rendering/TweenHost';
import type { TweenOptions } from '../../rendering/Tween';
import type { ISprite, SpriteOptions, WorldLayerHandle, VfxBlendMode } from '../../rendering/IRenderer';
import {
  bakeGraphicsToSprite,
  type BakeOptions,
} from '../atmosphere/util/bakeGraphicsToSprite';
import {
  drawMark,
  DECAL_HEIGHT_RATIO,
  type DecalSpec,
  type MarkFamily,
} from './decalBuilders';

// ── Constants (spec §2.2 / §5.2) ────────────────────────────────────────

/** Hard sprite cap — one draw-call band, trivial for Pixi. */
export const DECAL_CAP = 32;
/** Stamp-in: alpha 0→peak + scale 1.12→1.0 settle. */
export const DECAL_STAMP_IN_MS = 200;
/** Natural end-of-TTL fade-out. */
export const DECAL_FADE_OUT_MS = 2000;
/** Fast eviction fade when the ring wraps. */
export const DECAL_EVICT_FADE_MS = 300;
/** Same-family marks within this radius refresh TTL instead of spending a
 *  slot (anti-wallpaper, keeps AOE-spam honest). */
export const DECAL_MERGE_RADIUS_PX = 24;
export const DECAL_MERGE_ENABLED = true;
/** Size buckets for the texture cache (~4 buckets × 7 families × 1 biome). */
export const DECAL_SIZE_BUCKETS: readonly number[] = [40, 56, 80, 120];

export interface DecalSummary {
  active: number;
  evicted: number;
  cap: number;
  marks: Array<{ family: MarkFamily; school: string; x: number; y: number; ageMs: number }>;
}

/** Optional context for lifecycle records (the emit site passes it through). */
export interface DecalPlaceContext {
  abilityId?: string | null;
  targetId?: string | null;
  source?: string | null;
}

// ── Injectable substrate seams (structural mocks in tests — no Pixi) ────

export interface DecalLayerDeps {
  createLayer: (depth: number) => WorldLayerHandle;
  bake: (opts: BakeOptions) => ISprite | null;
  spriteFromTexture: (opts: SpriteOptions) => ISprite;
  addTween: (target: object, opts: TweenOptions) => TweenHostHandle;
  now: () => number;
}

const defaultDeps: DecalLayerDeps = {
  createLayer: (depth) => PixiOverlayHost.createWorldLayerContainer(depth),
  bake: (opts) => bakeGraphicsToSprite(opts),
  spriteFromTexture: (opts) => createSprite(null, opts),
  addTween: (target, opts) => TweenHost.add(target, opts),
  now: () => Date.now(),
};

interface ActiveMark {
  spec: DecalSpec;
  x: number;
  y: number;
  placedAt: number;
  sprite: ISprite | null;
  ttlHandle: TweenHostHandle | null;
  fadeHandle: TweenHostHandle | null;
  fading: boolean;
}

/** DecalSpec.blend → sprite substrate blend. `multiply` landed in
 *  VfxBlendMode (conductor 2026-07-17, passthrough to Pixi v8 native) —
 *  darkening families now truly darken the ground they sit on. */
function toSpriteBlend(blend: DecalSpec['blend']): VfxBlendMode {
  return blend;
}

function sizeBucketOf(width: number): number {
  let best = DECAL_SIZE_BUCKETS[0];
  for (const b of DECAL_SIZE_BUCKETS) {
    if (Math.abs(b - width) < Math.abs(best - width)) best = b;
  }
  return best;
}

/** djb2 — stable per-cache-key seed so a cached texture's shape is canonical
 *  (per-mark seed drives TRANSFORM jitter only, spec §5.1). */
function stableSeed(key: string): number {
  let h = 5381;
  for (let i = 0; i < key.length; i++) h = ((h << 5) + h + key.charCodeAt(i)) | 0;
  return h >>> 0;
}

export class DecalLayer {
  private static instance: DecalLayer | null = null;

  private readonly deps: DecalLayerDeps;
  private marks: ActiveMark[] = [];
  private evictedCount = 0;
  private layer: WorldLayerHandle | null = null;
  private layerAlpha = 1;
  /** cacheKey → registered textureKey (same string; presence = baked). */
  private textureCache = new Set<string>();
  private unsubMapSwitch: (() => void) | null = null;
  private initialized = false;

  constructor(deps?: Partial<DecalLayerDeps>) {
    this.deps = { ...defaultDeps, ...deps };
  }

  static getInstance(): DecalLayer {
    if (!this.instance) {
      this.instance = new DecalLayer();
      this.instance.init();
    }
    return this.instance;
  }

  /** Test-only: drop the singleton. */
  static __resetForTesting(): void {
    this.instance?.dispose();
    this.instance = null;
  }

  /** Full-teardown hook (VFXSystem.cleanup) — never CREATES an instance
   *  just to dispose it. */
  static disposeIfLive(): void {
    this.instance?.dispose();
    this.instance = null;
  }

  /**
   * Idempotent wiring: subscribe the in-place server map switch (the path
   * that runs NO VFX scene teardown — spec §5.3) and expose the headless
   * VQA read on window.__helixDebug.
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.unsubMapSwitch = EventBus.getInstance().on(
      EVENTS.MAP_SERVER_SWITCH_START,
      () => this.clear('map_switch'),
    );
    if (typeof window !== 'undefined') {
      const host = window as unknown as { __helixDebug?: Record<string, unknown> };
      host.__helixDebug = { ...(host.__helixDebug ?? {}), decals: () => this.summary() };
    }
    gameLogger.debug('vfx', '[DecalLayer] initialized (cap=' + DECAL_CAP + ')');
  }

  /**
   * Place a mark. Evicts the oldest at capacity; merges into a same-family
   * mark within DECAL_MERGE_RADIUS_PX by refreshing its TTL. Records a
   * `ground_mark` lifecycle event (single owner — emit sites must NOT
   * double-record). Degrades sprite-less in headless (marks still tracked,
   * so summary()/TTL semantics hold without a renderer).
   */
  place(spec: DecalSpec, x: number, y: number, ctx?: DecalPlaceContext): void {
    // Merge-refresh (spec §5.2) — same family close by: refresh, don't spend.
    if (DECAL_MERGE_ENABLED) {
      const near = this.marks.find(
        (m) => !m.fading && m.spec.family === spec.family
          && Math.hypot(m.x - x, m.y - y) <= DECAL_MERGE_RADIUS_PX,
      );
      if (near) {
        near.ttlHandle?.kill();
        near.ttlHandle = this.scheduleTtl(near, spec.ttlMs);
        near.placedAt = this.deps.now();
        this.recordLifecycle(spec, x, y, ctx, true);
        return;
      }
    }

    // Ring buffer: oldest evicted with a fast fade — leak-proof by capacity.
    while (this.marks.length >= DECAL_CAP) {
      const oldest = this.marks.shift();
      if (oldest) {
        this.evictedCount++;
        oldest.ttlHandle?.kill();
        this.beginFade(oldest, DECAL_EVICT_FADE_MS, /* tracked */ false);
      }
    }

    const mark: ActiveMark = {
      spec, x, y,
      placedAt: this.deps.now(),
      sprite: this.buildSprite(spec, x, y),
      ttlHandle: null,
      fadeHandle: null,
      fading: false,
    };
    mark.ttlHandle = this.scheduleTtl(mark, spec.ttlMs);
    this.marks.push(mark);
    this.recordLifecycle(spec, x, y, ctx, false);
  }

  /** Clear every mark immediately (map switch / scene change / manual). */
  clear(reason: 'map_switch' | 'scene_change' | 'manual'): void {
    for (const m of this.marks) this.destroyMark(m);
    this.marks = [];
    this.textureCache.clear();
    gameLogger.debug('vfx', `[DecalLayer] cleared (${reason})`);
  }

  /** Whole-aftermath-layer opacity dial (screenshot/VQA affordance). */
  setLayerAlpha(a: number): void {
    this.layerAlpha = a;
    this.layer?.setAlpha(a);
  }

  /** Headless/VQA debug read (window.__helixDebug.decals()). */
  summary(): DecalSummary {
    const now = this.deps.now();
    return {
      active: this.marks.length,
      evicted: this.evictedCount,
      cap: DECAL_CAP,
      marks: this.marks.map((m) => ({
        family: m.spec.family,
        school: m.spec.school,
        x: m.x,
        y: m.y,
        ageMs: now - m.placedAt,
      })),
    };
  }

  /** Full teardown (scene shutdown): clear marks, drop the container,
   *  unsubscribe. Conductor wires this into VFXSystem's scene cleanup. */
  dispose(): void {
    this.clear('scene_change');
    this.unsubMapSwitch?.();
    this.unsubMapSwitch = null;
    try { this.layer?.destroy(); } catch { /* already gone */ }
    this.layer = null;
    this.initialized = false;
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private ensureLayer(): WorldLayerHandle | null {
    if (this.layer) return this.layer;
    try {
      this.layer = this.deps.createLayer(Z_LAYERS.GROUND_DECALS);
      this.layer.setAlpha(this.layerAlpha);
    } catch {
      // Host not ready (cold mount / headless) — sprites fall back to the
      // default world container at the same depth; retry next place().
      this.layer = null;
    }
    return this.layer;
  }

  /**
   * Bake-once texture cache: first (family, school, core, bucket) bakes the
   * canonical-seed draw program under a stable textureKey; subsequent marks
   * mint a plain sprite off the registered texture (cache hit — no re-bake).
   */
  private buildSprite(spec: DecalSpec, x: number, y: number): ISprite | null {
    const bucket = sizeBucketOf(spec.width);
    const key = `decal_${spec.family}_${spec.school}_${spec.colorCore.toString(16)}_${bucket}`;
    const parent = this.ensureLayer() ?? undefined;
    const w = spec.width;
    const h = Math.round(w * DECAL_HEIGHT_RATIO);
    const spriteOpts: Omit<SpriteOptions, 'initialTextureKey'> = {
      x, y, width: w, height: h,
      depth: Z_LAYERS.GROUND_DECALS,
      alpha: 0,
      ...(parent ? { parent } : {}),
    };

    let sprite: ISprite | null = null;
    try {
      if (this.textureCache.has(key)) {
        sprite = this.deps.spriteFromTexture({ ...spriteOpts, initialTextureKey: key });
      } else {
        // Canonical texture shape: stable per-key seed. Per-mark seed only
        // drives the transform jitter below (spec §5.1).
        const canon: DecalSpec = { ...spec, width: bucket, seed: stableSeed(key) };
        sprite = this.deps.bake({
          draw: (gfx) => drawMark(gfx, canon),
          spriteOpts,
          textureKey: key,
        });
        if (sprite) this.textureCache.add(key);
      }
    } catch {
      sprite = null; // factory unregistered (headless) — track sprite-less
    }
    if (!sprite) return null;

    // NEVER style.glow — additive families use sprite blend inside this layer.
    sprite.setBlendMode(toSpriteBlend(spec.blend));

    // Seed jitter as transform, not texture: slight rotation + coin-flip mirror.
    const jitter = ((spec.seed % 1000) / 1000 - 0.5) * 12; // ±6°
    sprite.setAngle(jitter);
    sprite.setFlipX((spec.seed & 1) === 1);

    // Stamp-in: alpha 0→peak + scale 1.12→1.0 settle. One-shot, no repeat —
    // the ONLY animation a decal ever runs is stamp-in and fade-out.
    sprite.setScale(1.12);
    sprite.tween(
      { alpha: spec.alpha, scaleX: 1, scaleY: 1 },
      { durationMs: DECAL_STAMP_IN_MS, ease: 'sineOut' },
    );
    return sprite;
  }

  /** TTL rides the TweenHost clock — no bare timers (safeTimers hygiene). */
  private scheduleTtl(mark: ActiveMark, ttlMs: number): TweenHostHandle {
    return this.deps.addTween({ t: 0 }, {
      to: { t: 1 },
      durationMs: ttlMs,
      onComplete: () => this.beginFade(mark, DECAL_FADE_OUT_MS, true),
    });
  }

  private beginFade(mark: ActiveMark, durationMs: number, tracked: boolean): void {
    if (mark.fading) return;
    mark.fading = true;
    const done = () => {
      mark.sprite?.destroy();
      mark.sprite = null;
      if (tracked) {
        const i = this.marks.indexOf(mark);
        if (i >= 0) this.marks.splice(i, 1);
      }
    };
    if (mark.sprite) {
      mark.sprite.tween({ alpha: 0 }, { durationMs, onComplete: done });
    } else {
      // Sprite-less (headless): keep summary() timing honest via the clock.
      mark.fadeHandle = this.deps.addTween({ t: 0 }, {
        to: { t: 1 }, durationMs, onComplete: done,
      });
    }
  }

  private destroyMark(mark: ActiveMark): void {
    mark.ttlHandle?.kill();
    mark.fadeHandle?.kill();
    mark.sprite?.killAllTweens();
    mark.sprite?.destroy();
    mark.sprite = null;
  }

  private recordLifecycle(
    spec: DecalSpec, x: number, y: number,
    ctx: DecalPlaceContext | undefined, merged: boolean,
  ): void {
    VisualLifecycleStore.record({
      type: 'ground_mark',
      label: spec.family,
      x, y,
      abilityId: ctx?.abilityId ?? null,
      targetId: ctx?.targetId ?? null,
      school: spec.school,
      source: ctx?.source ?? 'decal',
      meta: { family: spec.family, ttlMs: spec.ttlMs, merged },
    });
  }
}
